// Copyright (c) 2026 aiApas LLC. All Rights Reserved.
// GuardRok — Proprietary and Confidential. Patent Pending.
// Unauthorized use or distribution is prohibited.

import { useState, useEffect } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "../context/AuthContext"
import { GuardRokIcon } from "../components/GuardRokLogo"
import { Eye, EyeOff } from "lucide-react"
import { supabase } from "../lib/supabase"

type Mode = "signin" | "forgot" | "reset"

export default function Login() {
  const { signIn } = useAuth()
  const [searchParams] = useSearchParams()
  const [mode, setMode]           = useState<Mode>("signin")
  const [email, setEmail]         = useState("")
  const [password, setPassword]   = useState("")
  const [newPassword, setNew]     = useState("")
  const [error, setError]         = useState("")
  const [info, setInfo]           = useState("")
  const [loading, setLoading]     = useState(false)
  const [showPassword, setShowPw] = useState(false)
  const [showNewPw,   setShowNewPw] = useState(false)

  // Detect Supabase recovery token in URL hash → switch to reset mode
  useEffect(() => {
    const hash = window.location.hash
    if (hash.includes("type=recovery") || hash.includes("type=invite")) {
      setMode("reset")
      window.history.replaceState(null, "", window.location.pathname)
    }
  }, [])

  // If redirected from signup because domain already exists → show info banner
  useEffect(() => {
    const domain = searchParams.get("hint")
    if (domain) setInfo(`Your company (${domain}) already has a GuardRok workspace — sign in below.`)
  }, [])

  // ── Sign in ──────────────────────────────────────────────
  async function handleSignIn(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true); setError("")
    const { error } = await signIn(email, password)
    if (error) setError(error.message)
    setLoading(false)
  }

  // ── Forgot password — send reset email ───────────────────
  async function handleForgot(e: React.FormEvent) {
    e.preventDefault()
    setLoading(true); setError(""); setInfo("")
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + "/login",
    })
    if (error) setError(error.message)
    else setInfo("Reset link sent — check your inbox (and spam folder).")
    setLoading(false)
  }

  // ── Reset password — set new password ────────────────────
  async function handleReset(e: React.FormEvent) {
    e.preventDefault()
    if (newPassword.length < 8) { setError("Password must be at least 8 characters."); return }
    setLoading(true); setError("")
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) setError(error.message)
    else { setInfo("Password set! Signing you in…"); setMode("signin") }
    setLoading(false)
  }

  function passwordStrength(pw: string) {
    let score = 0
    if (pw.length >= 8)        score++
    if (pw.length >= 12)       score++
    if (/[A-Z]/.test(pw))      score++
    if (/[0-9]/.test(pw))      score++
    if (/[^A-Za-z0-9]/.test(pw)) score++
    if (score <= 1) return { score, label: "Weak",   color: "#ef4444" }
    if (score <= 3) return { score, label: "Medium", color: "#f59e0b" }
    return                 { score, label: "Strong", color: "#22c55e" }
  }

  function generatePassword() {
    const up = "ABCDEFGHJKMNPQRSTUVWXYZ", lo = "abcdefghjkmnpqrstuvwxyz"
    const di = "23456789", sp = "!@#$%^&*"
    const all = up + lo + di + sp
    const pw = [up, lo, di, sp].map(s => s[Math.floor(Math.random() * s.length)])
    for (let i = 4; i < 14; i++) pw.push(all[Math.floor(Math.random() * all.length)])
    return pw.sort(() => Math.random() - 0.5).join("")
  }

  const inputCls = "w-full border border-gray-700 rounded-lg px-3 py-2.5 text-white text-sm outline-none focus:border-red-500"

  return (
    <div style={{minHeight:"100vh",background:"#030712"}} className="flex items-center justify-center p-4">
      <div className="w-full max-w-sm">

        {/* Logo */}
        <div className="flex items-center gap-3 justify-center mb-8">
          <GuardRokIcon size={44} />
          <div>
            <div style={{fontFamily:"system-ui,sans-serif",fontWeight:800,fontSize:22,color:"#fff",letterSpacing:"-0.5px",lineHeight:1}}>
              Guard<span style={{color:"#E63946"}}>Rok</span><span style={{fontSize:8,color:"#6b7280",verticalAlign:"super",marginLeft:1}}>™</span>
            </div>
            <div style={{fontSize:10,letterSpacing:"3px",color:"#6b7280",textTransform:"uppercase",marginTop:2}}>AI Firewall</div>
          </div>
        </div>

        <div style={{background:"#111827"}} className="rounded-2xl border border-gray-800 p-8">

          {/* ── Sign in ── */}
          {mode === "signin" && (
            <>
              <h1 className="text-white text-xl font-semibold mb-1">Sign in</h1>
              <p className="text-gray-500 text-sm mb-6">Access your security dashboard</p>
              <form onSubmit={handleSignIn} className="space-y-4">
                <div>
                  <label className="text-gray-400 text-xs font-medium uppercase tracking-wider block mb-1.5">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    style={{background:"#1f2937"}} className={inputCls}
                    placeholder="you@company.com" required />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">Password</label>
                    <button type="button" onClick={() => { setMode("forgot"); setError(""); setInfo("") }}
                      style={{background:"none",border:"none",cursor:"pointer",fontSize:12,color:"#e63946",padding:0,fontWeight:500}}>
                      Forgot password?
                    </button>
                  </div>
                  <div className="relative">
                    <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                      style={{background:"#1f2937"}} className={inputCls + " pr-10"}
                      placeholder="••••••••" required />
                    <button type="button" onClick={() => setShowPw(p => !p)}
                      style={{background:"transparent",border:"none",cursor:"pointer"}}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
                {error && <div style={{background:"#450a0a"}} className="border border-red-800 text-red-400 text-sm rounded-lg px-3 py-2">{error}</div>}
                {info  && <div style={{background:"#052e16"}} className="border border-green-800 text-green-400 text-sm rounded-lg px-3 py-2">{info}</div>}
                <button type="submit" disabled={loading}
                  className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-lg py-2.5 text-sm transition-colors">
                  {loading ? "Signing in…" : "Sign in"}
                </button>
              </form>
            </>
          )}

          {/* ── Forgot password ── */}
          {mode === "forgot" && (
            <>
              <h1 className="text-white text-xl font-semibold mb-1">Reset password</h1>
              <p className="text-gray-500 text-sm mb-6">We'll send a reset link to your email.</p>
              <form onSubmit={handleForgot} className="space-y-4">
                <div>
                  <label className="text-gray-400 text-xs font-medium uppercase tracking-wider block mb-1.5">Email</label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                    style={{background:"#1f2937"}} className={inputCls}
                    placeholder="you@company.com" required />
                </div>
                {error && <div style={{background:"#450a0a"}} className="border border-red-800 text-red-400 text-sm rounded-lg px-3 py-2">{error}</div>}
                {info  && <div style={{background:"#052e16"}} className="border border-green-800 text-green-400 text-sm rounded-lg px-3 py-2">{info}</div>}
                <button type="submit" disabled={loading}
                  className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-lg py-2.5 text-sm transition-colors">
                  {loading ? "Sending…" : "Send reset link"}
                </button>
                <button type="button" onClick={() => { setMode("signin"); setError(""); setInfo("") }}
                  style={{background:"none",border:"none",cursor:"pointer",width:"100%",color:"#6b7280",fontSize:13,marginTop:4,padding:0}}
                  className="hover:text-gray-400 transition-colors">
                  ← Back to sign in
                </button>
              </form>
            </>
          )}

          {/* ── Set new password (after clicking reset link) ── */}
          {mode === "reset" && (
            <>
              <h1 className="text-white text-xl font-semibold mb-1">Set new password</h1>
              <p className="text-gray-500 text-sm mb-6">Choose a password for your account.</p>
              <form onSubmit={handleReset} className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-gray-400 text-xs font-medium uppercase tracking-wider">New password</label>
                    <button type="button" onClick={() => setNew(generatePassword())}
                      style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:"#e63946",padding:0,fontWeight:600}}>
                      ✦ Suggest password
                    </button>
                  </div>
                  <div className="relative">
                    <input type={showNewPw ? "text" : "password"} value={newPassword} onChange={e => setNew(e.target.value)}
                      style={{background:"#1f2937"}} className={inputCls + " pr-10"}
                      placeholder="Min. 8 characters" required minLength={8} />
                    <button type="button" onClick={() => setShowNewPw(p => !p)}
                      style={{background:"transparent",border:"none",cursor:"pointer"}}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300 transition-colors">
                      {showNewPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {newPassword && (() => {
                    const s = passwordStrength(newPassword)
                    return (
                      <div style={{marginTop:8}}>
                        <div style={{height:4,background:"#1f2937",borderRadius:4,overflow:"hidden"}}>
                          <div style={{height:"100%",width:`${(s.score/5)*100}%`,background:s.color,borderRadius:4,transition:"all 0.3s"}} />
                        </div>
                        <div style={{fontSize:11,color:s.color,marginTop:4,fontWeight:600}}>{s.label}</div>
                      </div>
                    )
                  })()}
                </div>
                {error && <div style={{background:"#450a0a"}} className="border border-red-800 text-red-400 text-sm rounded-lg px-3 py-2">{error}</div>}
                {info  && <div style={{background:"#052e16"}} className="border border-green-800 text-green-400 text-sm rounded-lg px-3 py-2">{info}</div>}
                <button type="submit" disabled={loading}
                  className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-semibold rounded-lg py-2.5 text-sm transition-colors">
                  {loading ? "Saving…" : "Set password & sign in"}
                </button>
              </form>
            </>
          )}

        </div>

      </div>
    </div>
  )
}
